import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPage() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchBlogs = async () => {
      try {
        const res = await axios.get(`${API}/blogs`);
        setBlogs(res.data);
      } catch {
        console.error("Failed to fetch blogs");
      } finally {
        setLoading(false);
      }
    };
    fetchBlogs();
  }, []);

  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-muted/50" data-testid="blog-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-8">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-primary mb-4">
            Insights & Updates
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tighter text-foreground mb-4">
            Our Blog
          </h1>
          <p className="text-base text-muted-foreground font-light max-w-2xl">
            Expert insights on experiential learning, Bangalore school trips, and the future of education in India.
          </p>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-16 md:py-24" data-testid="blog-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="bg-secondary h-52 mb-4" />
                  <div className="bg-secondary h-4 w-1/3 mb-2" />
                  <div className="bg-secondary h-6 w-3/4 mb-2" />
                  <div className="bg-secondary h-4 w-full" />
                </div>
              ))}
            </div>
          ) : (
            <>
              {/* Featured Post */}
              {blogs.length > 0 && (
                <div
                  data-testid="blog-featured"
                  className="grid lg:grid-cols-2 gap-0 bg-card border border-border/60 overflow-hidden mb-12 group hover:shadow-xl transition-all duration-500"
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={blogs[0].image}
                      alt={blogs[0].title}
                      className="w-full h-64 lg:h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      loading="lazy"
                    />
                    <div className="absolute top-4 left-4 bg-primary text-primary-foreground px-3 py-1 text-xs font-medium rounded-sm">
                      Featured
                    </div>
                  </div>
                  <div className="p-8 md:p-12 flex flex-col justify-center">
                    <div className="flex items-center gap-4 mb-4">
                      <span className="text-xs font-medium text-primary uppercase tracking-wider">{blogs[0].category}</span>
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Clock className="w-3 h-3" /> {blogs[0].read_time}
                      </span>
                    </div>
                    <h2 className="font-heading text-2xl sm:text-3xl font-medium text-foreground mb-4 tracking-tight leading-tight">
                      {blogs[0].title}
                    </h2>
                    <p className="text-base text-muted-foreground font-light leading-relaxed mb-6">
                      {blogs[0].excerpt}
                    </p>
                    <span className="inline-flex items-center gap-2 text-sm font-medium text-primary cursor-default group-hover:gap-3 transition-all">
                      Read Article <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              )}

              {/* Rest of Posts */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {blogs.slice(1).map((post) => (
                  <article
                    key={post.id}
                    data-testid={`blog-post-${post.id}`}
                    className="bg-card border border-border/60 overflow-hidden group hover:shadow-lg transition-all duration-300"
                  >
                    <div className="relative overflow-hidden">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-52 object-cover group-hover:scale-105 transition-transform duration-700"
                        loading="lazy"
                      />
                      <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm px-2.5 py-1 text-xs font-medium text-primary rounded-sm">
                        {post.category}
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-xs text-muted-foreground">{post.date}</span>
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Clock className="w-3 h-3" /> {post.read_time}
                        </span>
                      </div>
                      <h3 className="font-heading text-lg font-medium text-foreground mb-2 leading-tight tracking-tight group-hover:text-primary transition-colors">
                        {post.title}
                      </h3>
                      <p className="text-sm text-muted-foreground font-light leading-relaxed line-clamp-3">
                        {post.excerpt}
                      </p>
                    </div>
                  </article>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
